import {
    Box,
    Button,
    FormHelperText,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    Form,
    Formik,
    FormikProps
} from "formik";
import OtpInput, { InputProps } from "react-otp-input";
import { MFSubmitButton } from ".";
import {
    useEffect,
    useRef,
    useState
} from "react";

interface Values {
    otp: string;
}

const initialValues: Values = {
    otp: "",
};

const OTP_TIMER = 60

export function OtpTimer({
    onResend,
    disabled,
    numberOfTries = 0
}: {
    onResend: () => void;
    disabled: boolean;
    numberOfTries?: number
}): JSX.Element {
    const [counter, setCounter] = useState(OTP_TIMER);

    useEffect(() => {
        let timer: any = null

        if (counter > 0) {
            timer = setInterval(() => {
                setCounter(counter - 1);
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [counter]);

    const handleResend = async () => {
        try {
            if (counter === 0) {
                await onResend();
                setCounter(OTP_TIMER);
            }
        } catch (e) {
            console.error((e as Error).message);
        }
    }

    return (
        <>
            {
                numberOfTries >= 3 ?
                    (
                        //number of limits exhauseted please try after 1 hour
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                mt: 1
                            }}
                        >
                            <Typography
                                variant='body_400'
                                color="text.secondary"
                            >
                                Resend OTP limits exhausted please try resending after 1 hour
                            </Typography>
                        </Box>
                    )
                    : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    mt: 1
                                }}
                            >
                                <Typography
                                    variant='body_400'
                                    color="text.secondary"
                                >
                                    Didn’t receive OTP ?
                                </Typography>
                                <Button
                                    variant="text"
                                    sx={(theme: Theme) => ({
                                        cursor: counter > 0 ? "not-allowed" : "pointer",
                                        color: counter > 0 ? theme.palette.text.secondary : theme.palette.primary[500],
                                        padding: "0px 5px",
                                        ...theme.typography.body_400
                                    })}
                                    onClick={handleResend}
                                    disabled={disabled || counter > 0}
                                >
                                    {/* {`Resend OTP${numberOfTries >= 2 ? '(Voice)' : '(SMS)'}`} */}
                                    Resend OTP (SMS)
                                </Button>
                            </Box>
                            {counter > 0 && (
                                <Typography
                                    variant='body_400'
                                    color="text.secondary"
                                >
                                    Resend in {counter} seconds
                                </Typography>
                            )}
                        </Box>
                    )
            }
        </>
    );
}

export default function OTPForm({
    onSubmit,
    onResend,
    otpMessage,
    warningMessage = "Note: User Account will be locked after 3 unsuccessful attempts, Resend otp can be requested 2 times for every hour",
    numberOfTries,
    heading = 'Verify OTP'
}: {
    onSubmit: (otp: string) => void;
    onResend: () => void;
    otpMessage: string;
    warningMessage?: string;
    numberOfTries?: number;
    heading?: string

}): JSX.Element {
    const theme = useTheme()
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const isXs = useMediaQuery(theme.breakpoints.up('xs'));
    const formikRef = useRef<FormikProps<Values>>(null);

    const [isLoading, setLoading] = useState<boolean>(false)
    const [isSubmitting, setSubmitting] = useState<boolean>(false)

    const handleResendOtp = async () => {
        try {
            setLoading(true)
            await onResend()
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setLoading(false)
        }
    };

    const handleChange = (otp: string) => {
        formikRef?.current?.setValues({ otp })
    }

    const handleSubmit = async (
        values: Values,
        { resetForm }: { resetForm: () => void }) => {
        try {
            setSubmitting(true)

            const { otp = "" } = values;

            if (!otp || otp?.length !== 6) {
                formikRef?.current?.setErrors({ otp: 'Enter OTP' })
                return;
            }

            await onSubmit(otp)
            resetForm()
        } catch (e) {
            console.error((e as Error).message);
            resetForm()
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ values, errors }) => (
                <Form noValidate>
                    <Typography
                        component="h1"
                        variant="h1"
                        sx={{
                            // fontFamily: 'Poppins, sans-serif',
                            // fontSize: 28,
                            // fontWeight: 600,
                            color: 'primary.main',
                            mb: '50px',
                            textAlign: 'center',
                        }}>
                        {heading ?? ''}
                    </Typography>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500],
                            textAlign: 'center',
                            marginBottom: "20px",
                        })}
                    >
                        {otpMessage ?? ""}
                    </Typography>
                    <Box
                        sx={{
                            mb: 0,
                            '.otp-input': {
                                input: {
                                    width: { xs: '37px !important', md: '45px !important' },
                                    fontSize: { xs: '20px !important', md: '30px !important' },
                                    margin: { xs: '2px !important', md: '4px !important' },
                                },
                            },
                        }}>
                        <OtpInput
                            inputType='number'
                            renderInput={(inputProps: InputProps) => <input {...inputProps} type='password' style={{ ...inputProps.style }} disabled={isLoading || isSubmitting} />}
                            value={values.otp}
                            // isDisabled={isLoading || isSubmitting}
                            onChange={handleChange}
                            numInputs={6}
                            // hasErrored={!!errors?.otp}
                            // isInputNum={true}
                            // isInputSecure={true}
                            shouldAutoFocus={true}
                            containerStyle={{
                                display: 'flex',
                                justifyContent: 'center',
                                // flexWrap: 'wrap',
                            }}
                            inputStyle={{
                                width: !isSm ? '35px' : !isMd ? '40px' : '45px',
                                height: !isSm ? '35px' : !isMd ? '40px' : '45px',
                                padding: '5px 10px',
                                borderRadius: '5px',
                                fontSize: '30px',
                                borderColor: errors?.otp ? `${theme.palette.common.red}` : `${theme.palette.text.primary}`,
                                color: errors?.otp ? `${theme.palette.common.red}` : `${theme.palette.text.primary}`,
                                outline: 'none'
                            }}
                            renderSeparator={() => (
                                <span style={{ width: '6px' }}></span>
                            )}
                        // errorStyle={{
                        //     border: `2px solid ${theme.palette.error[500]}`,
                        //     color: theme.palette.error[500],
                        // }}
                        // focusStyle={{ outline: 'none' }}
                        // className="otp-input"
                        />
                        {!!errors?.otp && (
                            <FormHelperText
                                error
                                sx={{
                                    mt: 2,
                                    textAlign: 'center',
                                    fontSize: '14px'
                                }}
                            >
                                {errors?.otp}
                            </FormHelperText>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: "5px 0px 10px 0px",
                        }}
                    >
                        <OtpTimer
                            onResend={handleResendOtp}
                            disabled={isLoading || isSubmitting}
                            numberOfTries={numberOfTries}
                        />
                    </Box>
                    <Box
                        sx={{
                            fontSize: 16,
                            color: 'red',
                            textAlign: 'center',
                            // marginBottom: -2,
                            marginBottom: 2
                        }}>
                        {/* Note: User Account will be locked after 3 unsuccessful attempts, Resend otp can be
              requested 2 times for every hour */}
                        {/* Note: You can request up to two SMS OTPs and one Voice OTP per hour. */}
                        Note: You can request up to three SMS OTPs per hour.
                    </Box>
                    <Typography
                        variant="subtitle_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.error[500],
                            textAlign: 'center',
                            marginBottom: "20px",
                        })}>
                        {warningMessage}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <MFSubmitButton
                            label="Submit"
                            disabled={isLoading}
                            loadingEnable={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    )
}
